import {
  useState,
  useCallback,
  createContext,
  useContext,
  ReactNode,
} from "react";

export type QueueItem = {
  id: string;
  vehicle: {
    model: string;
    modelCode: string;
    year: string;
    color: string;
    stockNumber: string;
    vin: string;
  };
};

export type PrintQueue = {
  queue: QueueItem[];
  value: number; // Queue length
  addToQueue: (item: QueueItem) => boolean; // Returns true if added, false if duplicate
  removeFromQueue: (id: string) => boolean; // Returns true if removed, false if not found
  clearQueue: () => void;
  isInQueue: (id: string) => boolean;
};

const PrintQueueContext = createContext<PrintQueue | null>(null);

export const PrintQueueProvider = ({ children }: { children: ReactNode }) => {
  const [queue, setQueue] = useState<QueueItem[]>([]);

  const addToQueue = useCallback(
    (item: QueueItem): boolean => {
      // Check if item with same ID already exists
      const exists = queue.some((queueItem) => queueItem.id === item.id);
      if (exists) {
        return false; // Item not added due to duplicate ID
      }

      if (queue.length >= 99) {
        return false; // Queue is full
      }

      setQueue((prev) => [...prev, item]);
      return true; // Item successfully added
    },
    [queue]
  );

  const removeFromQueue = useCallback((id: string): boolean => {
    let wasRemoved = false;

    setQueue((prev) => {
      const newQueue = prev.filter((item) => item.id !== id);
      wasRemoved = newQueue.length !== prev.length;
      return newQueue;
    });

    return wasRemoved;
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
  }, []);

  const isInQueue = useCallback(
    (id: string): boolean => {
      return queue.some((item) => item.id === id);
    },
    [queue]
  );

  return (
    <PrintQueueContext.Provider
      value={{
        queue,
        value: queue.length,
        addToQueue,
        removeFromQueue,
        clearQueue,
        isInQueue,
      }}
    >
      {children}
    </PrintQueueContext.Provider>
  );
};

export const usePrintQueue = () => {
  const context = useContext(PrintQueueContext);
  if (!context) {
    throw new Error(
      "usePrintQueueContext must be used within PrintQueueProvider"
    );
  }
  return context;
};
